import { clone, entries, intersection } from '@vestigas/utils';

import {
  DEFAULT_ROLE,
  INDIVIDUAL_ROLE,
  PERMISSION_ENTITIES,
  PERMISSIONS_AVAILABLE,
  PERMISSIONS_INITIAL,
} from './constants';
import {
  type PermissionCreatedObject,
  type PermissionEntity,
  type PermissionPermissionAvaliableKeys,
} from './types';

const arePermissionsEqual = (a: string[], b: string[]) => {
  if (a.length !== b.length) {
    return false;
  }

  return a
    .toSorted()
    .every((permission, index) => permission === b.toSorted()[index]);
};

/**
 * Utility function to set a nested permission value
 */
const setNestedPermission = (
  obj: Record<string, any>,
  path: string,
  value: boolean,
) => {
  const keys = path.split('.');
  let current: Record<string, any> = obj;

  for (let i = 0; i < keys.length - 1; i++) {
    const key = keys[i];
    if (!key) continue;

    if (!(key in current)) {
      current[key] = {};
    }

    current = current[key] as Record<string, any>;
  }

  const lastKey = keys.at(-1);
  if (lastKey) {
    current[lastKey] = value;
  }
};

/**
 * Utility function to get a nested permission value
 */
const getNestedPermission = (
  obj: Record<string, any>,
  path: string,
): boolean => {
  const keys = path.split('.');
  let current: any = obj;

  for (const key of keys) {
    if (current === null || current === undefined || !(key in current)) {
      return false;
    }

    current = current[key];
  }

  return Boolean(current);
};

export const PermissionsObject = {
  company: PERMISSIONS_INITIAL.COMPANY,
  costCenter: PERMISSIONS_INITIAL.COST_CENTER,
  dlnApproval: PERMISSIONS_INITIAL.DLN_APPROVAL,
  dlnBilling: PERMISSIONS_INITIAL.DLN_BILLING,
  dlnIn: PERMISSIONS_INITIAL.DLN_IN,
  dlnEsg: PERMISSIONS_INITIAL.DLN_ESG,
  invoice: PERMISSIONS_INITIAL.INVOICE,
  invoiceIn: PERMISSIONS_INITIAL.INVOICE_IN,
  invoiceOut: PERMISSIONS_INITIAL.INVOICE_OUT,
  organisationalGroup: PERMISSIONS_INITIAL.ORGANIZATIONAL_UNIT,
  site: PERMISSIONS_INITIAL.SITE,
  supplier: PERMISSIONS_INITIAL.SUPPLIER,
  user: PERMISSIONS_INITIAL.USER,
  userGroup: PERMISSIONS_INITIAL.USER_GROUP,
  vehicle: PERMISSIONS_INITIAL.VEHICLE,

  // Separate property for permission grant
  permissionGrant: false,

  /**
   * Create a new instance of the PermissionsObject with frontend permissions from the given backend permissions.
   */
  create(backendPermissions: string[] = []) {
    const instance = clone(this);
    if (backendPermissions.length === 0) {
      return instance;
    }

    // Handle special case: permission.grant
    if (backendPermissions.includes('access_control.grant')) {
      instance.permissionGrant = true;
    }

    for (const x of Object.keys(this.PERMISSION)) {
      if (
        intersection(
          this.PERMISSION[x as PermissionPermissionAvaliableKeys].BACKEND,
          backendPermissions,
        ).length === 0
      ) {
        continue;
      }

      const frontendPermissionPath =
        this.PERMISSION[x as PermissionPermissionAvaliableKeys].WEBAPP;

      // Skip the special permission.grant case as it's handled separately
      if (frontendPermissionPath === 'permission.grant') {
        continue;
      }

      setNestedPermission(instance, frontendPermissionPath, true);
    }

    return instance;
  },

  /**
   * Get granted frontend permissions.
   */
  getGrantedPermissions() {
    const grantedPermissions: string[] = [];

    // Handle special case: permission.grant
    if (this.permissionGrant) {
      grantedPermissions.push('permission.grant');
    }

    /**
     * Recursive tree traversal that walks through the nested permission object structure and collects all boolean values that are true, building dot-notation paths for each one.
     */
    const collectFromEntity = (
      entityObj: PermissionCreatedObject,
      entityName: PermissionEntity,
    ) => {
      for (const [permissionType, permissionObj] of entries(entityObj)) {
        if (permissionObj?._value) {
          grantedPermissions.push(`${entityName}.${permissionType}._value`);
        }

        if (permissionObj?.grant) {
          grantedPermissions.push(`${entityName}.${permissionType}.grant`);
        }
      }
    };

    // Apply to all entities
    for (const entity of PERMISSION_ENTITIES) {
      if (this[entity]) {
        collectFromEntity(this[entity], entity);
      }
    }

    return grantedPermissions.sort();
  },

  /**
   * Check if any permission is granted.
   */
  permissionGranted() {
    return this.getGrantedPermissions().length > 0;
  },

  /**
   * Get backend permissions based on granted frontend permissions.
   */
  getBackendPermissions() {
    const backendPermissions: string[] = [];

    // Handle special case: permission.grant
    if (this.permissionGrant) {
      backendPermissions.push('access_control.grant');
    }

    for (const permissionKey of Object.keys(this.PERMISSION)) {
      const frontendPath =
        this.PERMISSION[permissionKey as PermissionPermissionAvaliableKeys]
          .WEBAPP;

      // Skip the special permission.grant case as it's handled separately
      if (frontendPath === 'permission.grant') {
        continue;
      }

      if (getNestedPermission(this, frontendPath)) {
        const newBackendPermission =
          this.PERMISSION[permissionKey as PermissionPermissionAvaliableKeys]
            .BACKEND;

        backendPermissions.push(...newBackendPermission);
      }
    }

    return backendPermissions;
  },

  initPermissions(backendPermissions: string[] = []) {
    if (!backendPermissions) {
      return;
    }

    // Handle special case: permission.grant
    if (backendPermissions.includes('access_control.grant')) {
      this.permissionGrant = true;
    }

    for (const x of Object.keys(this.PERMISSION)) {
      if (
        intersection(
          this.PERMISSION[x as PermissionPermissionAvaliableKeys].BACKEND,
          backendPermissions,
        ).length === 0
      ) {
        continue;
      }

      const frontendPermissionPath =
        this.PERMISSION[x as PermissionPermissionAvaliableKeys].WEBAPP;

      // Skip the special permission.grant case as it's handled separately
      if (frontendPermissionPath === 'permission.grant') {
        continue;
      }

      setNestedPermission(this, frontendPermissionPath, true);
    }
  },

  /**
   * Set the permission values based on a given default role.
   */
  initWithDefaultRole(defaultRoleName: string) {
    if (!defaultRoleName) {
      return;
    }

    const defaultPermissions = Object.values(this.DEFAULT_ROLE).find(
      (defaultRole) => defaultRole.NAME === defaultRoleName,
    )?.PERMISSIONS;

    if (defaultPermissions) {
      // Handle special case: permission.grant
      if (defaultPermissions.includes('permission.grant')) {
        this.permissionGrant = true;
      }

      for (const defaultPermission of defaultPermissions) {
        // Skip the special permission.grant case as it's handled separately
        if (defaultPermission === 'permission.grant') {
          continue;
        }

        // Handle both simple permissions (site.read) and nested permissions (site.read.grant)
        // For simple permissions, we need to check if they should be mapped to _value
        const permissionEntry = Object.values(this.PERMISSION).find(
          (perm) =>
            perm.WEBAPP === defaultPermission ||
            perm.WEBAPP === `${defaultPermission}._value`,
        );

        if (permissionEntry) {
          setNestedPermission(this, permissionEntry.WEBAPP, true);
        } else {
          // Fallback to direct path setting for backward compatibility
          setNestedPermission(this, defaultPermission, true);
        }
      }
    }
  },

  getDefaultRoleName(): string | undefined {
    const grantedPermissions = this.getGrantedPermissions();

    if (grantedPermissions.length === 0) {
      return undefined;
    }

    const defaultRole = Object.entries(this.DEFAULT_ROLE).find(([_, value]) =>
      arePermissionsEqual(grantedPermissions, value.PERMISSIONS),
    )?.[1];

    if (defaultRole) {
      return defaultRole.NAME;
    }

    return this.INDIVIDUAL_ROLE;
  },

  /**
   * Get selectable roles.
   */
  getPickableRoles(): Array<{ id: string; name: string }> {
    const pickableRoles: Array<{ id: string; name: string }> = [
      {
        id: this.INDIVIDUAL_ROLE,
        name: this.INDIVIDUAL_ROLE,
      },
    ];

    for (const defaultRole of Object.values(this.DEFAULT_ROLE)) {
      pickableRoles.push({
        id: defaultRole.NAME,
        name: defaultRole.NAME,
      });
    }

    return pickableRoles;
  },

  /**
   * Check if the user can read (view) delivery notes.
   */
  canReadDeliveryNotes(): boolean {
    return Boolean((this as any).dlnIn?.read?._value);
  },

  // Static mapping of frontend permissions to backend permissions
  PERMISSION: PERMISSIONS_AVAILABLE,

  // Default roles and their associated permissions
  DEFAULT_ROLE,

  // Default role for individual users
  INDIVIDUAL_ROLE,
};
